/**
 * Font Style configuration row (PLAT-9973).
 *
 * One row per preset (Title / Subtitle / Text) inside Brand Kit → Fonts.
 * Each row shows the preset's currently-resolved family / weight / size
 * and lets the user override those values. Saving builds a
 * {@link FontStyleOverride} record that the parent persists onto
 * `branding.fontStyles`.
 *
 * Visual patterns (row chrome, family select, weight chips, save button)
 * intentionally match the other Brand Kit Fonts rows so the panel stays
 * cohesive.
 */

import { resolveFontVariant } from '@src/utility/fonts';
import { DEFAULT_FONT_STYLE_PRESETS } from '@src/utility/fontStyles';
import { useReducer, useState } from 'react';
import styled from 'styled-components';

import PencilIcon from '../../svgs/PencilIcon';

import type {
  FontFamily,
  FontStyleKey,
  FontStyleOverride,
} from '@src/types.ts/story';

type Props = {
  /** 'Title' | 'Subtitle' | 'Text' (Title Case). */
  presetLabel: string;
  /** Preset key (`'title'` etc.) — used as the override's `key`. */
  presetKey: FontStyleKey;
  /**
   * Existing override saved on the org branding, or `undefined` if the
   * org hasn't overridden this preset yet.
   */
  override: FontStyleOverride | undefined;
  /**
   * Default family / weight / size — used as the *initial* form values
   * (and the view-mode display) when no override exists. The picker on
   * Graphics → Text falls back to these same defaults via
   * `getFontStylePresets`.
   */
  defaults: {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
  };
  /** Available brand-uploaded font families to pick from. */
  fontFamilies: FontFamily[];
  /** Persist the override. Resolves once the round-trip completes. */
  onSave: (next: FontStyleOverride) => Promise<void>;
};

export const FontStyleConfigRow = ({
  presetLabel,
  presetKey,
  override,
  defaults,
  fontFamilies,
  onSave,
}: Props) => {
  const [isEditing, toggleEditing] = useReducer(s => !s, false);

  // Initial form values: override if present, otherwise the defaults.
  // `familyId` may be undefined when the override targets the default
  // family (a string, not a brand-kit `familyId`).
  const initialFamilyId = override?.familyId;
  const initialWeight = override?.fontWeight ?? defaults.fontWeight;
  const initialSize = override?.fontSize ?? defaults.fontSize;

  const [familyId, setFamilyId] = useState<string | undefined>(initialFamilyId);
  const [fontWeight, setFontWeight] = useState<number>(initialWeight);
  const [fontSize, setFontSize] = useState<number>(initialSize);
  const [isSaving, setIsSaving] = useState(false);

  const selectedFamily = fontFamilies.find(f => f.id === familyId);
  const activeVariants =
    selectedFamily?.variants.filter(v => !v.archivedAt) ?? [];
  // "Use regular weight only" was the user's framing earlier — they
  // still asked for weight to be configurable, so we expose normal-style
  // variants only (italic variants don't surface as Font Style choices).
  const normalVariants = activeVariants.filter(v => v.style === 'normal');

  // View-mode display values: family name resolves through the brand
  // kit lookup; falls back to the default family string when no
  // override family is configured or the family was deleted. When the
  // family is the unconfigured default we append " — Default" so the
  // row reads "Inter — Default" instead of an ambiguous "Inter" — the
  // user can see at a glance which rows they've actually customized.
  const resolvedOverrideFamilyName = override?.familyId
    ? fontFamilies.find(f => f.id === override.familyId)?.name
    : undefined;
  const displayFamilyName = resolvedOverrideFamilyName
    ? resolvedOverrideFamilyName
    : `${defaults.fontFamily} — Default`;
  const displaySize = override?.fontSize ?? defaults.fontSize;

  const defaultPreset = DEFAULT_FONT_STYLE_PRESETS.find(
    p => p.key === presetKey,
  );
  const previewFontFamily = resolvedOverrideFamilyName ?? defaults.fontFamily;
  const previewFontWeight = override?.fontWeight ?? defaults.fontWeight;
  const previewFontSize = defaultPreset?.previewFontSize ?? 14;
  const sample = defaultPreset?.sample ?? presetLabel;

  const handleFamilyChange = (nextId: string) => {
    const resolvedId = nextId || undefined;
    setFamilyId(resolvedId);
    if (!resolvedId) return;
    // Snap weight to the closest available variant in the new family.
    const newFamily = fontFamilies.find(f => f.id === resolvedId);
    if (!newFamily) return;
    const resolved = resolveFontVariant(newFamily, fontWeight, 'normal');
    if (resolved) {
      setFontWeight(resolved.weight);
    } else {
      const first = newFamily.variants.find(
        v => !v.archivedAt && v.style === 'normal',
      );
      if (first) setFontWeight(first.weight);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave({
      key: presetKey,
      familyId,
      fontWeight,
      fontSize,
    });
    setIsSaving(false);
    toggleEditing();
  };

  const handleCancel = () => {
    setFamilyId(initialFamilyId);
    setFontWeight(initialWeight);
    setFontSize(initialSize);
    toggleEditing();
  };

  if (isEditing) {
    return (
      <SRow>
        <SEditForm>
          <SPresetBadge>{presetLabel}</SPresetBadge>
          <SFamilySelect
            value={familyId ?? ''}
            onChange={e => handleFamilyChange(e.target.value)}
          >
            <option value="">Default — {defaults.fontFamily}</option>
            {fontFamilies.map(f => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </SFamilySelect>
          {normalVariants.length > 0 && (
            <SWeightPicker>
              {normalVariants.map(v => (
                <SWeightChip
                  key={v.artifactId}
                  active={fontWeight === v.weight}
                  type="button"
                  onClick={() => setFontWeight(v.weight)}
                >
                  {v.label}
                </SWeightChip>
              ))}
            </SWeightPicker>
          )}
          <SSizeField>
            <SSizeLabel htmlFor={`font-style-size-${presetKey}`}>
              Size (px)
            </SSizeLabel>
            <SSizeInput
              id={`font-style-size-${presetKey}`}
              type="number"
              min={8}
              max={400}
              step={1}
              value={fontSize}
              onChange={e => {
                const next = Number(e.target.value);
                if (Number.isFinite(next)) setFontSize(next);
              }}
            />
          </SSizeField>
          <SEditActions>
            <SActionBtn
              type="button"
              disabled={isSaving}
              onClick={handleCancel}
            >
              Cancel
            </SActionBtn>
            <SActionBtn
              type="button"
              className="save"
              disabled={isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </SActionBtn>
          </SEditActions>
        </SEditForm>
      </SRow>
    );
  }

  return (
    <SRow>
      <SPreview
        style={{
          fontFamily: previewFontFamily,
          fontWeight: previewFontWeight,
          fontSize: `${previewFontSize}px`,
        }}
      >
        {sample}
      </SPreview>
      <SMeta>
        <SPresetBadge>{presetLabel}</SPresetBadge>
        <SFamilyLabel>{displayFamilyName}</SFamilyLabel>
        <SValueLabel>{displaySize} px</SValueLabel>
        <SEditBtn type="button" onClick={toggleEditing} aria-label="Edit">
          <PencilIcon width="18" height="18" strokeColor="var(--accent)" />
        </SEditBtn>
      </SMeta>
    </SRow>
  );
};

export default FontStyleConfigRow;

const SRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--surface-elevated);
  border-radius: 8px;
  padding: 12px 16px;
`;

const SPreview = styled.span`
  display: block;
  color: var(--foreground);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SPresetBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid var(--accent);
  color: var(--accent);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  min-width: 88px;
  justify-content: center;
`;

const SFamilyLabel = styled.span`
  flex: 1;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SValueLabel = styled.span`
  flex-shrink: 0;
  height: 28px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  background: var(--border);
  color: var(--text-secondary);
  font-size: var(--text-xs);
`;

const SEditBtn = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: var(--transition-normal);
  &:hover {
    background: var(--border);
  }
`;

const SEditForm = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SFamilySelect = styled.select`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: var(--accent);
  }
`;

const SWeightPicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SWeightChip = styled.button<{ active: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid
    ${({ active }) => (active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ active }) =>
    active ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'};
  color: ${({ active }) =>
    active ? 'var(--accent)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-normal);
  &:hover {
    border-color: var(--accent);
  }
`;

const SSizeField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SSizeLabel = styled.label`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const SSizeInput = styled.input`
  width: 96px;
  height: 36px;
  padding: 0 12px;
  background: none;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  outline: none;
  &:focus {
    border-color: var(--accent);
  }
`;

const SEditActions = styled.div`
  display: flex;
  align-self: flex-end;
  gap: 8px;
`;

const SActionBtn = styled.button`
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  transition: var(--transition-normal);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text-secondary);
  &:hover:not(:disabled) {
    border-color: var(--text-muted);
    color: var(--foreground);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &.save {
    background: var(--primary);
    color: var(--primary-foreground);
    border-color: var(--primary);
    &:hover:not(:disabled) {
      background: var(--primary-hover);
      border-color: var(--primary-hover);
      color: var(--primary-foreground);
    }
  }
`;
